import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { PopupNames } from 'core';
import { useDebtorCandidateIds, usePopupStateOperations } from 'core/hooks';
import { PATHS } from 'router/config';

import { LmsBlockBadge } from 'components/molecules/LmsBlockBadge';

import { formatMoneyCurrency } from 'components/pages/_detailShared/format';
import { StudentColors } from 'components/pages/StudentDetail/parts/studentColors';
import { StudentRosterFilters } from 'components/pages/Students/parts/StudentRosterFilters';
import { RosterExportButton } from 'components/pages/Students/parts/RosterExportButton';
import {
  EMPTY_ROSTER_FILTER,
  filterRoster,
  sortRoster,
  IRosterFilterState,
  IStudentListRow,
  IStudentSort,
  StudentSortKey,
} from 'core/utils/studentRoster';

import { IStudent, IStudentAgreement } from 'types';

import {
  DebtorsTableComponent,
  IStudentsColumn,
  IStudentsRow,
} from './StudentsTable';
import { DebtCell } from './DebtCell';

/** State surfaced to the page for the heading count + eager-load decision. */
export interface IRosterState {
  filteredCount: number;
  hasActiveFilter: boolean;
}

export interface IStudentsTable {
  data: IStudent[];
  loading?: boolean;
  error?: string;
  loadMore: () => void;
  hasMore: boolean;
  /** Finance-view permission — gates the roster export control (Req 21.5). */
  canExportFinance?: boolean;
  /** Reports the filtered count + active-filter state for the page heading. */
  onRosterStateChange?: (state: IRosterState) => void;
}

/** Map a roster agreement row to the pure-layer `IStudentListRow` shape. */
const toListRow = (
  a: IStudentAgreement,
  candidateIds: Set<string>,
): IStudentListRow => ({
  id: a.id,
  agreementId: a.agreementId,
  name: a.name,
  contractTitle: a.contractTitle,
  phone: a.phone,
  groupName: a.group || '',
  currency: a.currency || '',
  budgetAmount: a.budgetRaw ?? a.budgetAmount,
  paidAmount: a.paidRaw ?? a.paidAmount,
  terminated: a.terminated,
  isLmsBlocked: Boolean(a.isLmsBlocked),
  isDebtorCandidate: candidateIds.has(a.id),
});

const SORTABLE: Partial<Record<string, StudentSortKey>> = {
  name: 'name',
  group: 'group',
  paidAmount: 'paid',
  debt: 'debt',
};

/** Clickable header with an asc/desc indicator for a sortable column. */
const SortHeader = ({
  label,
  sortKey,
  sort,
  onToggle,
}: {
  label: string;
  sortKey: StudentSortKey;
  sort: IStudentSort | null;
  onToggle: (key: StudentSortKey) => void;
}) => {
  const active = sort?.key === sortKey;
  const arrow = !active ? '' : sort?.dir === 'asc' ? ' ↑' : ' ↓';
  return (
    <HeaderButton
      type="button"
      active={active}
      onClick={() => onToggle(sortKey)}
      aria-sort={
        active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      {label}
      {arrow}
    </HeaderButton>
  );
};

const buildColumns = (
  t: TFunction,
  sort: IStudentSort | null,
  onToggle: (key: StudentSortKey) => void,
): IStudentsColumn[] => {
  const header = (accessor: string, labelKey: string) => {
    const key = SORTABLE[accessor];
    const label = `${t(labelKey)}`;
    return key ? (
      <SortHeader label={label} sortKey={key} sort={sort} onToggle={onToggle} />
    ) : (
      label
    );
  };

  return [
    {
      Header: header('name', 'table.students.name'),
      accessor: 'name',
      flexible: true,
    },
    {
      Header: header('contractTitle', 'table.students.contractTitle'),
      accessor: 'contractTitle',
      columnWidth: '150px',
      marginRight: '32px',
      marginLeft: '32px',
    },
    {
      Header: header('group', 'table.students.group'),
      accessor: 'group',
      columnWidth: '80px',
    },
    {
      Header: header('phone', 'table.students.phone'),
      accessor: 'phone',
      columnWidth: '120px',
      marginRight: '32px',
      marginLeft: '32px',
    },
    {
      Header: header('budgetAmount', 'table.students.budgetAmount'),
      accessor: 'budgetAmount',
      columnWidth: '104px',
      marginRight: '24px',
    },
    {
      Header: header('paidAmount', 'table.students.paidAmount'),
      accessor: 'paidAmount',
      columnWidth: '104px',
      marginRight: '24px',
    },
    {
      Header: header('debt', 'table.students.debt'),
      accessor: 'debt',
      columnWidth: '120px',
      marginRight: '80px',
    },
  ];
};

export const StudentsTable = memo(
  ({
    data,
    loading,
    error,
    loadMore,
    hasMore,
    canExportFinance = false,
    onRosterStateChange,
  }: IStudentsTable) => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
      operations: { showPopup },
    } = usePopupStateOperations();

    const [filter, setFilter] = useState<IRosterFilterState>(
      EMPTY_ROSTER_FILTER,
    );
    const [sort, setSort] = useState<IStudentSort | null>(null);

    // Company-wide debtor-candidate ids feed the «должники без блокировки»
    // option only — skipped until that filter is actually selected.
    const {
      ids: candidateIds,
      loading: candidatesLoading,
    } = useDebtorCandidateIds({
      skip: filter.lms !== 'debtorsNoBlock',
    });

    const onToggleSort = useCallback((key: StudentSortKey) => {
      setSort((prev) =>
        prev && prev.key === key
          ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
          : { key, dir: 'asc' },
      );
    }, []);

    // The full loaded roster as pure-layer rows (search is already applied
    // server-side, so the client query argument stays empty here).
    const listRows = useMemo<IStudentListRow[]>(
      () =>
        (data as IStudentAgreement[]).map((a) => toListRow(a, candidateIds)),
      [data, candidateIds],
    );

    const visibleRows = useMemo<IStudentListRow[]>(
      () => sortRoster(filterRoster(listRows, filter, ''), sort),
      [listRows, filter, sort],
    );

    const hasActiveFilter =
      filter.debt !== 'any' ||
      filter.status !== 'any' ||
      filter.lms !== 'any' ||
      filter.group !== null ||
      filter.currency !== null;

    // While the candidate set is still loading under the debtorsNoBlock
    // filter the roster can't be classified yet — treat the table as loading
    // so no premature «ничего не найдено» flashes.
    const isLoading =
      Boolean(loading) ||
      (filter.lms === 'debtorsNoBlock' && candidatesLoading);

    useEffect(() => {
      onRosterStateChange?.({
        filteredCount: visibleRows.length,
        hasActiveFilter,
      });
    }, [visibleRows.length, hasActiveFilter, onRosterStateChange]);

    const columns = useMemo(() => buildColumns(t, sort, onToggleSort), [
      t,
      sort,
      onToggleSort,
    ]);

    const rows = useMemo<IStudentsRow[]>(
      () =>
        visibleRows.map((row) => {
          const currency = row.currency || '';
          return ({
            id: row.id,
            studentId: row.id,
            agreementId: row.agreementId,
            name: row.isLmsBlocked ? (
              <>
                {row.name}
                <LmsBlockBadge />
              </>
            ) : (
              row.name
            ),
            contractTitle: row.contractTitle,
            group: row.groupName,
            phone: row.phone,
            budgetAmount: formatMoneyCurrency(
              Number(row.budgetAmount) || 0,
              currency,
            ),
            paidAmount: formatMoneyCurrency(
              Number(row.paidAmount) || 0,
              currency,
            ),
            debt: (
              <DebtCell
                budgetAmount={row.budgetAmount}
                paidAmount={row.paidAmount}
                currency={currency}
                terminated={row.terminated}
              />
            ),
            terminated: row.terminated,
          } as unknown) as IStudentsRow;
        }),
      [visibleRows],
    );

    const onClickEdit = useCallback(
      ({ agreementId, studentId }: IStudentAgreement) => {
        showPopup(PopupNames.editAgreement, { agreementId, studentId });
      },
      [showPopup],
    );

    const onClickAddAgreement = useCallback(
      (studentId: string) => {
        showPopup(PopupNames.studentPayment, { studentId });
      },
      [showPopup],
    );

    const onClickOpenDetail = useCallback(
      (studentId: string) => {
        history.push(PATHS.studentDetail.replace(':id', studentId));
      },
      [history],
    );

    if (error) {
      return <p>{error}</p>;
    }

    const isFilteredEmpty =
      !isLoading && visibleRows.length === 0 && listRows.length > 0;

    // The «К блокировке» filter is overdue-invoice based, a narrower set than
    // plan-debt — a dedicated empty message reads as «no overdue invoices to
    // act on» rather than a generic «filter matched nothing».
    const emptyStateMessage =
      filter.lms === 'debtorsNoBlock'
        ? t('pages.students.filters.lms.emptyState')
        : t('pages.students.filters.emptyState');

    return (
      <Layout>
        <Toolbar>
          <StudentRosterFilters
            rows={listRows}
            filter={filter}
            onChange={setFilter}
          />
          <RosterExportButton
            rows={visibleRows}
            canExportFinance={canExportFinance}
          />
        </Toolbar>

        {isFilteredEmpty ? (
          <EmptyState role="status">{emptyStateMessage}</EmptyState>
        ) : (
          <DebtorsTableComponent
            rows={rows}
            columns={columns}
            isFlexible
            noDataText={t('table.students.noData')}
            loadMore={loadMore}
            hasMore={hasMore}
            loading={isLoading}
            onClickEdit={onClickEdit}
            onClickAddAgreement={onClickAddAgreement}
            onClickOpenDetail={onClickOpenDetail}
          />
        )}
      </Layout>
    );
  },
);

const Layout = styled.div`
  width: 100%;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  width: 100%;
`;

const EmptyState = styled.div`
  width: 100%;
  padding: 48px 0;
  text-align: center;
  color: ${StudentColors.textMuted};
  font-size: 14px;
`;

const HeaderButton = styled.button<{ active: boolean }>`
  border: none;
  background: transparent;
  font: inherit;
  font-weight: ${({ active }) => (active ? 700 : 600)};
  color: ${({ active }) =>
    active ? StudentColors.accent : StudentColors.textSecondary};
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
`;
